/** @format */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useBrand } from "../../hooks/useBrand";
import { useAuth } from "../../hooks/useAuth";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useDeleteQuestion } from "../../hooks/useDeleteQuestion";
import { useUpdateQuestionStatus } from "../../hooks/useUpdateQuestionStatus";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { FLAG_SUBJECT_EXPERT_APPROVAL_QUEUE } from "../../featureFlags/constants";
import { AdminQuestionPreviewModal } from "../../components/AdminQuestionPreviewModal";
import { DeleteConfirmModal } from "../../components/DeleteConfirmModal";
import { EditPartModal } from "../../components/EditPartModal";
import { ProxiedImage } from "../../components/ProxiedImage";
import { QuestionContentBlocks } from "../../components/QuestionContentBlocks";
import {
	Question,
	QuestionSeries,
	SearchParams,
	QuestionStatus,
	Aggregations,
	getPublishBlockers,
	isPreLive,
	isSubjectMatterExpert,
	getParentQuestionNumber,
	getQuestionContentBlocks,
} from "@question-bank/shared";
import { useParentGroup } from "../../hooks/useParentGroup";
import { usePaperById } from "../../hooks/usePapersWithAttachments";
import { paperIdOf } from "../../utils/attachments";
import { searchQuestions, getAssetUrl, updateQuestion, getRejectionReason } from "../../api/questions";
import { useTranslation } from "react-i18next";
import { toStringArray } from "../../utils/helpers";
import { ClassificationReview, type TaxonomySelection } from "../../components/ClassificationReview";
import { MathText } from "../../components/MathText";
import { Locale } from "../../features/i18n/init";
import { AdminQuestionChangeHistoryModal } from "../../components/AdminQuestionChangeHistoryModal";

type DisplayStatus = QuestionStatus | "deleted";

const STATUS_STYLES: Record<DisplayStatus, string> = {
	draft: "bg-amber-100 text-amber-800",
	staged: "bg-blue-100 text-blue-800",
	published: "bg-green-100 text-green-800",
	deleted: "bg-red-100 text-red-800",
};

/** A non-expert moves a draft to staged. Only an expert moves a question to published. */
const nextReviewStatus = (status: QuestionStatus, isExpert: boolean): QuestionStatus => {
	if (status === "published") {
		return "draft";
	}
	if (status === "draft" && !isExpert) {
		return "staged";
	}
	return "published";
};

const StatusBadge = ({ status }: { status: DisplayStatus }) => (
	<span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[status]}`}>
		{status}
	</span>
);

const DetailSkeleton = () => (
	<div className="animate-pulse space-y-6" aria-label="Loading question">
		<div className="h-6 w-48 rounded bg-slate-200" />
		<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised) space-y-3">
			{Array.from({ length: 8 }).map((_, i) => (
				<div key={i} className="flex gap-4">
					<div className="h-4 w-28 rounded bg-slate-200" />
					<div className="h-4 w-28 rounded bg-slate-200" />
				</div>
			))}
		</div>
		<div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
			{Array.from({ length: 3 }).map((_, i) => (
				<div key={i} className="aspect-4/3 rounded-lg bg-slate-200" />
			))}
		</div>
	</div>
);

interface ImageLightboxProps {
	src: string;
	alt: string;
	onClose: () => void;
}

const ImageLightbox = ({ src, alt, onClose }: ImageLightboxProps) => {
	const closeRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		closeRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Image lightbox"
				className="relative max-h-full max-w-4xl"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					ref={closeRef}
					type="button"
					onClick={onClose}
					aria-label="Close lightbox"
					className="absolute -top-10 right-0 text-white text-sm font-semibold hover:underline"
				>
					Close ✕
				</button>
				<ProxiedImage src={src} alt={alt} className="max-h-[85vh] w-full rounded-lg object-contain" loading="eager" />
			</div>
		</div>
	);
};

const formatSeries = (s?: QuestionSeries): string => {
	if (!s) {
		return "-";
	}
	return `${s.season.title ?? s.season.code} ${s.year}`;
};

const formatLanguage = (lang: string): string => (lang === "cy" ? "Welsh" : "English");

export const AdminViewQuestionPage = () => {
	const { id } = useParams<{ id: string }>();
	useDocumentTitle(id ? `Admin – View Question ${id}` : "Admin – View Question");
	const location = useLocation();
	const navigate = useNavigate();
	const { brand } = useBrand();
	const { user } = useAuth();
	const { t } = useTranslation();
	const canShowApprovalQueue = useFeatureFlag(FLAG_SUBJECT_EXPERT_APPROVAL_QUEUE);
	const isExpert = isSubjectMatterExpert(user?.group);
	const { locale } = useParams<{ locale: Locale }>();
	const { parent: question, items, children, isLoading, error, reload } = useParentGroup(brand, id!);
	// Paper-level facts (subject, level, tier, series, date) live on the owning
	// Paper (QB2-223); resolve it by the question's paperId for display below.
	const paper = usePaperById(brand, question ? paperIdOf(question) : undefined);
	const { isDeleting, error: deleteError, deleteQuestion, reset: resetDelete } = useDeleteQuestion(brand);
	const {
		isUpdating: isUpdatingStatus,
		error: statusError,
		updateQuestionStatus,
		reset: resetStatusError,
	} = useUpdateQuestionStatus(brand);

	const [isPendingDelete, setIsPendingDelete] = useState(false);
	const [statusOverride, setStatusOverride] = useState<QuestionStatus | null>(null);
	const [statusSuccessMessage, setStatusSuccessMessage] = useState<string | null>(null);
	const [rejectionReason, setRejectionReason] = useState("");
	const [storedRejectionReason, setStoredRejectionReason] = useState<string | undefined>();
	const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
	const [lightboxAlt, setLightboxAlt] = useState<string>("");
	const [editingPart, setEditingPart] = useState<Question | null>(null);
	const [partPendingDelete, setPartPendingDelete] = useState<Question | null>(null);
	const [aggregations, setAggregations] = useState<Aggregations>({ subject: [] });
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const deleteTriggerRef = useRef<HTMLButtonElement>(null);
	const previewTriggerRef = useRef<HTMLButtonElement>(null);
	const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [isHistoryLoading, setIsHistoryLoading] = useState(false);

	useEffect(() => {
		return () => {
			if (statusTimerRef.current) {
				clearTimeout(statusTimerRef.current);
			}
		};
	}, []);

	// Aggregations power the embedded edit form's dropdowns (subject/level/etc).
	useEffect(() => {
		let isCancelled = false;
		searchQuestions(brand, {})
			.then((response) => {
				if (!isCancelled) {
					setAggregations(response.aggregations ?? { subject: [] });
				}
			})
			.catch(() => {
				/* dropdowns degrade gracefully to empty options */
			});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	const returnState = (location.state as { filters?: SearchParams } | null)?.filters
		? { filters: (location.state as { filters: SearchParams }).filters }
		: undefined;

	const handleDeleteClick = useCallback(() => {
		resetDelete();
		setIsPendingDelete(true);
	}, [resetDelete]);

	const handleCancelDelete = useCallback(() => {
		setIsPendingDelete(false);
		deleteTriggerRef.current?.focus();
	}, []);

	const handleClassificationSelect = useCallback(
		async (selection: TaxonomySelection) => {
			if (!question) {
				return;
			}
			// A label-only choice invalidates any pinned Core identity.
			await updateQuestion(question.id, brand, {
				specification: { theme: selection.theme, topic: selection.topic, subTopic: selection.subTopic },
			});
			reload();
		},
		[question, brand, reload],
	);

	const handlePreviewClick = useCallback(() => {
		setIsPreviewOpen(true);
	}, []);

	const handleClosePreview = useCallback(() => {
		setIsPreviewOpen(false);
		previewTriggerRef.current?.focus();
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!question) {
			return;
		}
		const isDeleted = await deleteQuestion(question.id);
		if (isDeleted) {
			navigate(`/${locale}/admin/questions`);
		}
	}, [question, deleteQuestion, navigate, locale]);

	const openLightbox = useCallback((src: string, alt: string) => {
		setLightboxSrc(src);
		setLightboxAlt(alt);
	}, []);

	const closeLightbox = useCallback(() => {
		setLightboxSrc(null);
	}, []);

	useEffect(() => {
		const status = statusOverride ?? question?.status;
		if (!question || status !== "draft") {
			setStoredRejectionReason(undefined);
			return;
		}
		let isCurrent = true;
		void getRejectionReason(brand, question.id).then((reason) => {
			if (isCurrent) {
				setStoredRejectionReason(reason);
			}
		});
		return () => {
			isCurrent = false;
		};
	}, [brand, question, statusOverride]);

	const handleStatusToggle = useCallback(async () => {
		if (!question || question.deleted) {
			return;
		}
		const currentStatus: QuestionStatus = statusOverride ?? question.status ?? "published";
		const newStatus = nextReviewStatus(currentStatus, isExpert);

		setStatusSuccessMessage(null);
		resetStatusError();

		if (statusTimerRef.current) {
			clearTimeout(statusTimerRef.current);
		}

		const isSuccess = await updateQuestionStatus(question.id, newStatus);
		if (isSuccess) {
			setStatusOverride(newStatus);
			const message =
				newStatus === "published"
					? "Question published successfully."
					: newStatus === "staged"
						? "Question staged successfully."
						: "Question unpublished successfully.";
			setStatusSuccessMessage(message);
			statusTimerRef.current = setTimeout(() => {
				setStatusSuccessMessage(null);
			}, 3000);
		}
	}, [question, statusOverride, isExpert, updateQuestionStatus, resetStatusError]);

	const handleReject = useCallback(async () => {
		if (!question) {
			return;
		}
		const reason = rejectionReason.trim();
		if (!reason) {
			return;
		}
		setStatusSuccessMessage(null);
		resetStatusError();
		const isSuccess = await updateQuestionStatus(question.id, "draft", reason);
		if (isSuccess) {
			setStatusOverride("draft");
			setStoredRejectionReason(reason);
			setRejectionReason("");
			setStatusSuccessMessage(t("approvalQueue.rejected"));
		}
	}, [question, rejectionReason, resetStatusError, t, updateQuestionStatus]);

	const handlePartStatusToggle = useCallback(
		async (part: Question) => {
			if (part.deleted) {
				return;
			}
			const partStatus: QuestionStatus = part.status ?? "published";
			const newStatus = nextReviewStatus(partStatus, isExpert);
			resetStatusError();
			const isSuccess = await updateQuestionStatus(part.id, newStatus);
			if (isSuccess) {
				reload();
			}
		},
		[isExpert, updateQuestionStatus, resetStatusError, reload],
	);

	const handleConfirmPartDelete = useCallback(async () => {
		if (!partPendingDelete) {
			return;
		}
		const isDeleted = await deleteQuestion(partPendingDelete.id);
		if (isDeleted) {
			setPartPendingDelete(null);
			reload();
		}
	}, [partPendingDelete, deleteQuestion, reload]);

	const handleOpenHistory = useCallback(() => {
		setIsHistoryLoading(true);
		setIsHistoryOpen(true);
	}, []);

	const handleCloseHistory = useCallback(() => {
		setIsHistoryOpen(false);
		setIsHistoryLoading(false);
	}, []);

	if (isLoading) {
		return (
			<section className="space-y-6">
				<DetailSkeleton />
			</section>
		);
	}

	if (error || !question) {
		return (
			<section className="space-y-6">
				<div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-(--admin-shadow-raised)">
					<p className="font-semibold text-red-700"> Failed to load question</p>
					<p className="mt-1 text-sm text-red-600">{error ?? "Question not found."}</p>
					<Link
						to={`/${locale}/admin/questions`}
						state={returnState}
						className="mt-4 inline-block text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
					>
						← Back to questions
					</Link>
				</div>
			</section>
		);
	}

	const imageItems: { src: string; label: string }[] = [
		{ src: question.en?.markSchemeImage ?? "", label: "Mark scheme image (English)" },
		{ src: question.cy?.markSchemeImage ?? "", label: "Mark scheme image (Welsh)" },
	]
		.filter((item) => item.src)
		.map((item) => ({ ...item, src: getAssetUrl(brand, item.src) }));
	const displayStatus: DisplayStatus = question.deleted
		? "deleted"
		: (statusOverride ?? question.status ?? "published");

	const publishBlockers = getPublishBlockers(question, { parts: children });
	const canPublish = publishBlockers.length === 0;
	const isBlockPublish = isPreLive(displayStatus) && !canPublish;
	const isStagedWithoutExpert = displayStatus === "staged" && !isExpert;
	const isDraftForNonExpert = displayStatus === "draft" && !isExpert;

	const metadataRows: [string, string][] = [
		["Subject", toStringArray(paper?.subject).join(", ") || "-"],
		["Level", paper?.qualification ? (paper.qualification.title ?? paper.qualification.code) : "-"],
		["Series", formatSeries(paper?.series)],
		["Paper Code", question.paperCode ?? "-"],
		["Question No.", question.questionNumber ?? "-"],
		["Language", formatLanguage(question.language)],
		["Tier", paper?.tier ? (paper.tier.title ?? paper.tier.code) : "-"],
		["Brand", question.brand ?? "-"],
		["Marks", typeof question.maxMarks === "number" ? String(question.maxMarks) : "-"],
	];

	return (
		<section className="space-y-6">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<Link
						to={`/${locale}/admin/questions`}
						state={returnState}
						className="text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
					>
						← Back to questions
					</Link>
					<div className="mt-2 flex flex-wrap items-center gap-3">
						<h2 className="text-[1.75rem] font-bold text-(--admin-color-primary)">
							{toStringArray(paper?.subject).join(", ") || "Question"} ({question?.questionNumber})
						</h2>
						<StatusBadge status={displayStatus} />
					</div>
				</div>
				<div className="flex shrink-0 flex-col items-end gap-2">
					<div className="flex gap-3">
						<button
							ref={previewTriggerRef}
							type="button"
							onClick={handlePreviewClick}
							className="cursor-pointer rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
						>
							Preview
						</button>
						{displayStatus !== "deleted" && (
							<button
								type="button"
								onClick={() => void handleStatusToggle()}
								disabled={isUpdatingStatus || isStagedWithoutExpert || (isBlockPublish && !isDraftForNonExpert)}
								title={
									isStagedWithoutExpert
										? t("approvalQueue.expertOnly")
										: isBlockPublish
											? publishBlockers.join("; ")
											: undefined
								}
								className={`cursor-pointer rounded-md px-4 py-2 text-sm font-semibold shadow-sm disabled:cursor-not-allowed disabled:opacity-50 ${
									isPreLive(displayStatus)
										? "bg-amber-600 text-white hover:bg-green-700"
										: "bg-amber-600 text-white hover:bg-amber-700"
								}`}
							>
								{isUpdatingStatus
									? "Updating…"
									: displayStatus === "published"
										? "Unpublish"
										: isDraftForNonExpert
											? t("approvalQueue.stage")
											: "Publish"}
							</button>
						)}
					</div>
					{canShowApprovalQueue && displayStatus === "staged" && (
						<form
							className="flex w-full max-w-xs flex-col items-end gap-2"
							onSubmit={(event) => {
								event.preventDefault();
								void handleReject();
							}}
						>
							<label htmlFor="rejection-reason" className="text-xs font-semibold text-slate-600">
								{t("approvalQueue.reasonLabel")}
							</label>
							<textarea
								id="rejection-reason"
								value={rejectionReason}
								onChange={(event) => setRejectionReason(event.target.value)}
								required
								rows={3}
								maxLength={1000}
								className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800"
							/>
							<button
								type="submit"
								disabled={isUpdatingStatus || rejectionReason.trim().length === 0}
								className="cursor-pointer rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{t("approvalQueue.reject")}
							</button>
						</form>
					)}
					{storedRejectionReason && displayStatus === "draft" && (
						<p className="max-w-xs text-right text-sm text-slate-700" role="status">
							<span className="font-semibold">{t("approvalQueue.reasonHeading")}: </span>
							{storedRejectionReason}
						</p>
					)}
					{isBlockPublish && (
						<div className="max-w-xs text-right text-xs text-red-600" role="alert">
							<p className="font-semibold">Cannot publish yet:</p>
							<ul className="list-inside list-disc">
								{publishBlockers.map((blocker) => (
									<li key={blocker}>{blocker}</li>
								))}
							</ul>
						</div>
					)}
					<div className="flex gap-3">
						<Link
							to={`/${locale}/admin/questions/${question.id}/edit`}
							className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
						>
							Edit
						</Link>
						<button
							ref={deleteTriggerRef}
							type="button"
							onClick={handleDeleteClick}
							className="cursor-pointer rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
						>
							Delete
						</button>
						<button
							onClick={handleOpenHistory}
							disabled={isHistoryLoading}
							className="cursor-pointer rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
						>
							{isHistoryLoading ? "Loading..." : "View history"}
						</button>
					</div>
				</div>
			</div>

			{statusSuccessMessage && (
				<div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-center">
					<p className="text-base font-semibold text-green-700">{statusSuccessMessage}</p>
				</div>
			)}
			{statusError && (
				<div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center">
					<p className="text-base font-semibold text-red-700">{statusError}</p>
				</div>
			)}

			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
				<h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">Details</h3>
				<dl className="space-y-2 text-sm">
					{metadataRows.map(([label, value]) => (
						<div key={label} className="flex gap-2">
							<dt className="w-36 shrink-0 font-medium text-slate-500">{label}</dt>
							<dd className="text-slate-800">{value}</dd>
						</div>
					))}
				</dl>
			</div>

			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
						Question Parts {children.length > 0 ? `(${children.length})` : ""}
					</h3>
				</div>
				{children.length === 0 ? (
					<p className="text-sm text-slate-500">This question has no additional parts.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
									<th scope="col" className="px-3 py-2 whitespace-nowrap">
										Part
									</th>
									<th scope="col" className="px-3 py-2">
										Marks
									</th>
									<th scope="col" className="px-3 py-2">
										Status
									</th>
									<th scope="col" className="px-3 py-2 text-right">
										<span className="sr-only">Actions</span>
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{children.map((part) => {
									const partStatus: DisplayStatus = part.deleted ? "deleted" : (part.status ?? "published");
									// A part that itself has sub-parts (e.g. `1(a)` → `1(a)(i)`) is a
									// grouped parent, so pass its direct children for validation.
									const partChildren = items.filter(
										(item) => getParentQuestionNumber(item.questionNumber) === part.questionNumber,
									);
									const partBlockers = getPublishBlockers(part, { parts: partChildren });
									const isPartBlockPublish = isPreLive(partStatus) && partBlockers.length > 0;
									const isPartWithoutExpert = partStatus === "staged" && !isExpert;
									const isPartDraftForNonExpert = partStatus === "draft" && !isExpert;
									return (
										<tr key={part.id} className="hover:bg-slate-50">
											<td className="px-3 py-2 font-medium text-slate-800">{part.questionNumber}</td>
											<td className="px-3 py-2 text-slate-700">
												{typeof part.maxMarks === "number" ? part.maxMarks : "-"}
											</td>
											<td className="px-3 py-2">
												<StatusBadge status={partStatus} />
											</td>
											<td className="px-3 py-2">
												<div className="flex items-center justify-end gap-3 whitespace-nowrap">
													<button
														type="button"
														onClick={() => setEditingPart(part)}
														className="text-xs font-semibold text-(--admin-color-primary-strong) hover:underline"
													>
														Edit
													</button>
													{partStatus !== "deleted" && (
														<>
															<button
																type="button"
																onClick={() => void handlePartStatusToggle(part)}
																disabled={
																	isUpdatingStatus ||
																	isPartWithoutExpert ||
																	(isPartBlockPublish && !isPartDraftForNonExpert)
																}
																title={
																	isPartWithoutExpert
																		? t("approvalQueue.expertOnly")
																		: isPartBlockPublish
																			? partBlockers.join("; ")
																			: undefined
																}
																className="text-xs font-semibold text-amber-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
															>
																{partStatus === "published"
																	? "Unpublish"
																	: isPartDraftForNonExpert
																		? t("approvalQueue.stage")
																		: "Publish"}
															</button>
															<button
																type="button"
																onClick={() => setPartPendingDelete(part)}
																className="text-xs font-semibold text-red-600 hover:underline"
															>
																Delete
															</button>
														</>
													)}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{getQuestionContentBlocks(question).length > 0 && (
				<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
					<h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">Question Content</h3>
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
						<div>
							<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Content (English)</h4>
							<QuestionContentBlocks
								question={question}
								language="en"
								brand={brand}
								containerClassName="flex flex-col gap-3"
								textClassName="whitespace-pre-wrap text-sm leading-6 text-slate-700"
								imageClassName="w-full rounded-md object-contain"
							/>
						</div>
						<div>
							<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Content (Welsh)</h4>
							<QuestionContentBlocks
								question={question}
								language="cy"
								brand={brand}
								containerClassName="flex flex-col gap-3"
								textClassName="whitespace-pre-wrap text-sm leading-6 text-slate-700"
								imageClassName="w-full rounded-md object-contain"
							/>
						</div>
					</div>
				</div>
			)}

			{(question.en?.examinersComments || question.cy?.examinersComments) && (
				<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
					<h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">Examiner's Comments</h3>
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
						{question.en?.examinersComments && (
							<div>
								<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
									Comments (English)
								</h4>
								<p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
									<MathText text={question.en.examinersComments} />
								</p>
							</div>
						)}
						{question.cy?.examinersComments && (
							<div>
								<h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Comments (Welsh)</h4>
								<p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
									<MathText text={question.cy.examinersComments} />
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{imageItems.length > 0 && (
				<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
					<h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">Images</h3>
					<ul className="grid grid-cols-2 gap-4 sm:grid-cols-3" aria-label="Question images">
						{imageItems.map((item) => (
							<li key={item.label}>
								<p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</p>
								<button
									type="button"
									onClick={() => openLightbox(item.src, item.label)}
									aria-label={`View ${item.label} full size`}
									className="w-full overflow-hidden rounded-lg focus:outline-none focus:ring-2 focus:ring-(--admin-color-primary-strong)"
								>
									<ProxiedImage src={item.src} alt={item.label} className="w-full rounded-lg object-cover" />
								</button>
							</li>
						))}
					</ul>
				</div>
			)}

			{question.classification && (
				<ClassificationReview
					proposal={question.classification}
					current={question.specification}
					taxonomy={aggregations.specification ?? []}
					onSelect={handleClassificationSelect}
				/>
			)}

			{lightboxSrc && <ImageLightbox src={lightboxSrc} alt={lightboxAlt} onClose={closeLightbox} />}
			{editingPart && (
				<EditPartModal
					brand={brand}
					partId={editingPart.id}
					partNumber={editingPart.questionNumber}
					parentNumber={question.questionNumber}
					childParts={items.filter(
						(item) => getParentQuestionNumber(item.questionNumber) === editingPart.questionNumber,
					)}
					aggregations={aggregations}
					onClose={() => setEditingPart(null)}
					onReload={reload}
					onRequestDelete={() => {
						const part = editingPart;
						setEditingPart(null);
						setPartPendingDelete(part);
					}}
					onSaved={() => {
						setEditingPart(null);
						reload();
					}}
				/>
			)}
			{partPendingDelete && (
				<DeleteConfirmModal
					question={partPendingDelete}
					subject={paper?.subject}
					isDeleting={isDeleting}
					error={deleteError}
					onConfirm={() => void handleConfirmPartDelete()}
					onCancel={() => setPartPendingDelete(null)}
				/>
			)}
			{isPendingDelete && (
				<DeleteConfirmModal
					question={question}
					subject={paper?.subject}
					isDeleting={isDeleting}
					error={deleteError}
					onConfirm={() => void handleConfirmDelete()}
					onCancel={handleCancelDelete}
				/>
			)}
			{isPreviewOpen && (
				<AdminQuestionPreviewModal parent={question} items={items} paper={paper} onClose={handleClosePreview} />
			)}
			{isHistoryOpen && question && (
				<AdminQuestionChangeHistoryModal onClose={handleCloseHistory} questionId={question.id} />
			)}
		</section>
	);
};
